import { useEffect, useState } from "react"
import { toTodayInputDate } from "@/domain/common/utils/DateUtils"
import { EntryClient } from "@/domain/entry/client/EntryClient"
import { PHONE_NO_PATTERN } from "@/domain/entry/model/Entry"
import { fightSportPayloadToJSON } from "@bracket/idl/main/fight/model/enums/FightSportPt"
import { TournamentClient } from "@/domain/tournament/client/TournamentClient"
import { useWeightClassChoices } from "@/domain/fight/hooks/WeightClassChoicesHook"
import { isPlayerRecordCollectedByValue } from "@/domain/fight/model/Fight"
import type {
  EntryDetail,
  EntryGymErrors,
  EntryGymInput,
  EntryPlayerErrors,
  EntryPlayerInput,
  EntryPlayerTextField,
} from "@/domain/entry/model/Entry"
import type { TournamentDetail } from "@/domain/tournament/model/Tournament"

/**
 * 조회 결과(라벨 문자열)를 폼 입력값(enum 이름 문자열)으로 되돌린다.
 *
 * 성별은 라벨("남")만으로는 enum 을 되돌릴 수 없어 조회 모델의 라벨을 매핑한다.
 * 서버가 내려주는 값만 쓰므로 화면이 임의로 만들어 채우지 않는다.
 */
/** 체급 선택지가 달라지는 입력 — 이 값이 바뀌면 부 · 성별이 바뀌므로 다시 묻는다 */
const WEIGHT_CLASS_TRIGGER_FIELDS: EntryPlayerTextField[] = [
  "sport",
  "birthDate",
  "gender",
]

function toGenderValue(genderLabel: string): string {
  if (genderLabel === "남") return "GENDER_MALE"
  if (genderLabel === "여") return "GENDER_FEMALE"

  return ""
}

function toPlayerInputs(detail: EntryDetail): EntryPlayerInput[] {
  return detail.players.map((player) => ({
    key: player.id || crypto.randomUUID(),
    sport: fightSportPayloadToJSON(player.sport),
    name: player.name,
    birthDate: player.birthDate,
    gender: toGenderValue(player.genderLabel),
    weightKg: player.weightKg,
    careerYears: player.careerYears,
    competitionCount: player.competitionCount,
    // 저장된 체급은 선택지를 불러온 뒤 표시명으로 다시 고른다 (payload 는 서버가 준 값만 쓴다)
    weightClass: null,
  }))
}

function toGymInput(detail: EntryDetail): EntryGymInput {
  return {
    name: detail.gymName,
    contactName: detail.gymContactName,
    contactPhoneNo: detail.gymContactPhoneNo,
  }
}

function makeInitPlayer(): EntryPlayerInput {
  return {
    key: crypto.randomUUID(),
    sport: "",
    name: "",
    birthDate: "",
    gender: "",
    weightKg: "",
    careerYears: "",
    competitionCount: "",
    weightClass: null,
  }
}

/**
 * 참가 신청 수정 훅.
 *
 * 조회와 같은 인증(연락처 + 비밀번호)을 다시 받아 확인한 뒤 폼을 채운다 —
 * 수정 화면에 직접 들어와도 본인만 고칠 수 있어야 한다.
 * 저장은 부분 수정이 아니라 **선수 목록 전체 교체**다.
 */
export function useEntryModify(entryNo: string) {
  const [gymContactPhoneNo, setGymContactPhoneNo] = useState("")
  const [password, setPassword] = useState("")
  const [detail, setDetail] = useState<EntryDetail | null>(null)
  const [tournament, setTournament] = useState<TournamentDetail | null>(null)
  const [gym, setGym] = useState<EntryGymInput>({
    name: "",
    contactName: "",
    contactPhoneNo: "",
  })
  const [players, setPlayers] = useState<EntryPlayerInput[]>([])
  const [gymErrors, setGymErrors] = useState<EntryGymErrors>({})
  const [playerErrors, setPlayerErrors] = useState<
    Record<string, EntryPlayerErrors>
  >({})
  const [errorMessage, setErrorMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isModified, setIsModified] = useState(false)
  const { getChoices, loadChoices } = useWeightClassChoices()

  function setGymField(field: keyof EntryGymInput, value: string) {
    setGym((current) => ({ ...current, [field]: value }))
  }

  function setPlayerField(
    key: string,
    field: EntryPlayerTextField,
    value: string,
  ) {
    // 종목 · 생년월일 · 성별이 바뀌면 고를 체급이 달라진다 — 고른 값을 버리고 다시 묻는다
    const isWeightClassReset = WEIGHT_CLASS_TRIGGER_FIELDS.includes(field)

    setPlayers((current) =>
      current.map((player) => {
        if (player.key !== key) return player
        if (isWeightClassReset) {
          return { ...player, [field]: value, weightClass: null }
        }

        return { ...player, [field]: value }
      }),
    )

    if (!isWeightClassReset) return

    const changedPlayer = players.find((player) => player.key === key)
    if (!changedPlayer) return

    void loadChoices({
      tournamentId: detail?.tournamentId ?? "",
      ...changedPlayer,
      [field]: value,
    })
  }

  function setPlayerWeightClass(key: string, optionKey: string) {
    const selected = getChoices(key).options.find(
      (option) => option.key === optionKey,
    )

    setPlayers((current) =>
      current.map((player) =>
        player.key === key
          ? { ...player, weightClass: selected ?? null }
          : player,
      ),
    )
  }

  function addPlayer() {
    setPlayers((current) => [...current, makeInitPlayer()])
  }

  function removePlayer(key: string) {
    setPlayers((current) => current.filter((player) => player.key !== key))
  }

  /**
   * 저장된 체급을 선택지에서 다시 고른다
   *
   * 조회 응답에는 체급 **표시명**만 있고 그 값이 선택 키라, 선택지를 불러온 뒤 이름으로 맞춘다.
   */
  async function restoreWeightClasses(
    playerInputs: EntryPlayerInput[],
    loadedDetail: EntryDetail,
  ) {
    const savedNameByKey = new Map(
      loadedDetail.players.map((player) => [player.id, player.weightClassName]),
    )

    await Promise.all(
      playerInputs.map(async (player) => {
        const options = await loadChoices({
          tournamentId: loadedDetail.tournamentId,
          ...player,
        })
        const savedName = savedNameByKey.get(player.key) ?? ""
        const saved = options.find((option) => option.key === savedName)
        if (!saved) return

        setPlayers((current) =>
          current.map((eachPlayer) =>
            eachPlayer.key === player.key
              ? { ...eachPlayer, weightClass: saved }
              : eachPlayer,
          ),
        )
      }),
    )
  }

  /** 본인 확인 후 폼을 채운다 */
  async function load() {
    if (!PHONE_NO_PATTERN.test(gymContactPhoneNo.replaceAll(" ", ""))) {
      setErrorMessage("체육관 연락처 형식을 확인해 주세요.")
      return
    }
    if (!password) {
      setErrorMessage("신청 비밀번호를 입력해 주세요.")
      return
    }

    setIsLoading(true)

    try {
      const loadedDetail = await EntryClient.getByEntryNo({
        entryNo,
        gymContactPhoneNo,
        password,
      })
      const loadedTournament = await TournamentClient.getById(
        loadedDetail.tournamentId,
      )

      const playerInputs = toPlayerInputs(loadedDetail)

      setDetail(loadedDetail)
      setTournament(loadedTournament)
      setGym(toGymInput(loadedDetail))
      setPlayers(playerInputs)
      setErrorMessage("")

      void restoreWeightClasses(playerInputs, loadedDetail)
    } catch (error) {
      setDetail(null)
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "신청 내역을 불러오지 못했습니다.",
      )
    } finally {
      setIsLoading(false)
    }
  }

  function validate(): string {
    const nextGymErrors: EntryGymErrors = {}
    const nextPlayerErrors: Record<string, EntryPlayerErrors> = {}

    if (!gym.name.trim()) nextGymErrors.name = "체육관명을 입력해 주세요."
    if (!gym.contactName.trim()) {
      nextGymErrors.contactName = "담당자명을 입력해 주세요."
    }
    if (!PHONE_NO_PATTERN.test(gym.contactPhoneNo.replaceAll(" ", ""))) {
      nextGymErrors.contactPhoneNo = "휴대폰 번호 형식을 확인해 주세요."
    }

    players.forEach((player) => {
      const errors: EntryPlayerErrors = {}
      const isFutureBirthDate =
        Boolean(player.birthDate) && player.birthDate > toTodayInputDate()
      const hasWeightClassChoice = getChoices(player.key).options.length > 0
      // 닭싸움은 이름 · 생년월일 · 성별 · 종목 넷만 받는다 — 체급 · 체중 칸이 없다
      const isRecordCollected = isPlayerRecordCollectedByValue(player.sport)

      if (isRecordCollected && hasWeightClassChoice && !player.weightClass) {
        errors.weightClass = "체급을 선택해 주세요."
      }
      if (!player.sport) errors.sport = "종목을 선택해 주세요."
      if (!player.name.trim()) errors.name = "이름을 입력해 주세요."
      if (!player.birthDate) errors.birthDate = "생년월일을 입력해 주세요."
      if (isFutureBirthDate) {
        errors.birthDate = "생년월일은 오늘 이후로 입력할 수 없습니다."
      }
      if (!player.gender) errors.gender = "성별을 선택해 주세요."
      if (
        isRecordCollected &&
        (player.weightKg === "" || Number(player.weightKg) <= 0)
      ) {
        errors.weightKg = "0보다 큰 몸무게를 입력해 주세요."
      }
      if (Object.keys(errors).length > 0) nextPlayerErrors[player.key] = errors
    })

    setGymErrors(nextGymErrors)
    setPlayerErrors(nextPlayerErrors)

    if (players.length === 0) return "선수를 1명 이상 남겨 주세요."
    if (
      Object.keys(nextGymErrors).length > 0 ||
      Object.keys(nextPlayerErrors).length > 0
    ) {
      return "필수 입력 항목을 확인해 주세요."
    }

    return ""
  }

  async function submit() {
    const validationMessage = validate()

    if (validationMessage) {
      setErrorMessage(validationMessage)
      return
    }

    setIsLoading(true)

    try {
      await EntryClient.modify({
        entryNo,
        gymContactPhoneNo,
        password,
        gym,
        players,
      })
      setErrorMessage("")
      setIsModified(true)
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "신청 수정에 실패했습니다.",
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setDetail(null)
    setIsModified(false)
    setErrorMessage("")
  }, [entryNo])

  return {
    gymContactPhoneNo,
    password,
    detail,
    tournament,
    gym,
    players,
    gymErrors,
    playerErrors,
    errorMessage,
    isLoading,
    isModified,
    setGymContactPhoneNo,
    setPassword,
    setGymField,
    setPlayerField,
    setPlayerWeightClass,
    getWeightClassChoices: getChoices,
    addPlayer,
    removePlayer,
    load,
    submit,
  }
}
